import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RestaurantService } from './restaurant.service';

export interface CartItemEditTarget {
    route: string;
    params: any;
}

interface EditSource {
    menu: any;
    product: any;
}

interface EditSession {
    index: number;
    id: number;
    type: string;
}

@Injectable({ providedIn: 'root' })
export class CartItemEditService {

    private static readonly ROTAS_QUE_CONFIRMAM_EDICAO = ['extra', 'pizza-intermediary', 'order-summary', 'pizza', 'pizza-nep'];

    private session: EditSession | null = null;

    constructor(
        private restaurantService: RestaurantService,
        private router: Router,
    ) {}

    get isEditing(): boolean {
        return this.session !== null;
    }

    canEdit(item): boolean {
        if (item?.points_needed) return false;

        return !!this.sourceFor(item);
    }

    targetFor(item): CartItemEditTarget | null {
        if (item?.points_needed) return null;

        const source = this.sourceFor(item);
        if (!source) return null;

        if (item.type === 'pizza') return this.pizzaTarget(item, source);
        if (item.type === 'combo') return this.comboTarget(item, source);

        return this.generalTarget(item, source);
    }

    start(index: number, item: any): void {
        this.session = { index, id: item?.id, type: item?.type };
    }

    cancel(): void {
        this.session = null;
    }

    consume(products: any[], incoming: any): number | null {
        const session = this.session;
        this.session = null;

        if (!session) return null;
        if (!this.confirmedFromItemFlow()) return null;
        if (!this.isSameItem(session, incoming)) return null;
        if (!this.isSameItem(session, products[session.index])) return null;

        return session.index;
    }

    private confirmedFromItemFlow(): boolean {
        const path = (this.router.url || '').split(/[?#]/)[0];
        const route = path.split('/').filter(Boolean).pop() ?? '';

        return CartItemEditService.ROTAS_QUE_CONFIRMAM_EDICAO.includes(route);
    }

    private isSameItem(session: EditSession, item: any): boolean {
        return !!item && item.id === session.id && item.type === session.type;
    }

    private sourceFor(item): EditSource | null {
        const menu = this.restaurantService.restaurant?.menu;
        if (!item || !menu) return null;

        if (item.type === 'pizza') {
            return this.sourceOf(menu, (menu.pizzas?.sizes ?? []).find(size => size.id === item.id));
        }

        if (item.type === 'combo') {
            const combo = (menu.combos ?? []).find(menuCombo => menuCombo.id === item.id);
            if (!this.comboStillMatchesMenu(item, combo)) return null;

            return this.sourceOf(menu, combo);
        }

        const product = this.findGeneralProduct(menu, item.id);
        if (!product) return null;

        if (!this.hasNewComplementsFlow() && this.extraGroupsOf(product, menu).length === 0) return null;

        return this.sourceOf(menu, product);
    }

    private comboStillMatchesMenu(item, combo): boolean {
        if (!combo) return false;

        const sizesNoCardapio = combo.fromNEP
            ? (combo.pizzaAssociations ?? []).length
            : (combo.pizzaSizes ?? []).length;

        return (item.comboItems ?? []).length === sizesNoCardapio;
    }

    private sourceOf(menu, product): EditSource | null {
        if (!product) return null;

        return { menu, product };
    }

    private pizzaTarget(item, source: EditSource): CartItemEditTarget {
        return {
            route: this.pizzaRoute(),
            params: {
                'product': source.product,
                'isCombo': false,
                'menu': source.menu,
                'pizzaTemplate': this.clone(item),
            },
        };
    }

    private comboTarget(item, source: EditSource): CartItemEditTarget {
        return {
            route: this.pizzaRoute(),
            params: {
                'product': source.product,
                'isCombo': true,
                'menu': source.menu,
                'reopenComboTemplate': this.clone(item),
            },
        };
    }

    private generalTarget(item, source: EditSource): CartItemEditTarget {
        return {
            route: 'extra',
            params: {
                'extras': this.clone(this.extraGroupsOf(source.product, source.menu)),
                'item': this.clone(source.product),
                'editing': {
                    'extras': item.extras ?? [],
                    'qty': item.qty,
                    'notes': item.notes,
                },
            },
        };
    }

    private findGeneralProduct(menu, productID) {
        for (const category of menu.general ?? []) {
            const product = (category.products ?? []).find(menuProduct => menuProduct.id === productID);
            if (product) return product;
        }

        return null;
    }

    private extraGroupsOf(product, menu) {
        const orderLevelEnabled = !!this.modules()?.order_level_extras;

        return (product.extras ?? [])
            .map(extraID => (menu.extras ?? []).filter(menuExtra =>
                menuExtra.id == extraID && !(orderLevelEnabled && menuExtra.isOrderLevel)
            )[0] ?? null)
            .filter(extra => extra?.options.length > 0);
    }

    private hasNewComplementsFlow(): boolean {
        return !!this.modules()?.new_complements_pedir_delivery;
    }

    private pizzaRoute(): string {
        if (!this.modules()?.new_nep_pizzas) return 'pizza';

        return this.hasNewComplementsFlow() ? 'pizza-intermediary' : 'pizza-nep';
    }

    private modules() {
        return this.restaurantService.restaurant?.info?.modules;
    }

    private clone(value) {
        return typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));
    }
}
